import {useCallback, useRef, useState} from 'react';

interface UseInViewOptions {
    /** Stop observing after the first time the element enters the viewport. */
    triggerOnce?: boolean;
    /** IntersectionObserver threshold(s) — fraction(s) of the element that must be visible. */
    threshold?: number | number[];
    /** IntersectionObserver rootMargin, e.g. '50px' to trigger slightly before entry. */
    rootMargin?: string;
    /** Value used on the server and before the first observation. Defaults to false. */
    initialInView?: boolean;
}

type UseInViewResult = [(node?: Element | null) => void, boolean];

/**
 * Minimal `useInView` built on the native IntersectionObserver.
 *
 * API-compatible with the slice of `react-intersection-observer`'s `useInView` that
 * AnimatedComponent relies on: it returns a `[ref, inView]` tuple where `ref` is a
 * callback ref. Only the options AnimatedComponent passes are supported
 * (`triggerOnce`, `threshold`, `rootMargin`) — this is intentionally not a full
 * reimplementation of the library.
 *
 * A callback ref (not a ref object) is required because AnimatedComponent swaps the
 * rendered element (plain tag → framer-motion component) once it becomes visible; the
 * callback ref re-attaches the observer to the new node on that swap.
 */
export function useInView({triggerOnce = false, threshold, rootMargin, initialInView = false}: UseInViewOptions = {}): UseInViewResult {
    const [inView, setInView] = useState(initialInView);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const setRef = useCallback(
        (node?: Element | null) => {
            // Tear down any existing observer first — handles node swap and unmount,
            // guaranteeing at most one live observer per hook instance.
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }

            // Detach only, or environment without IntersectionObserver (SSR safety net).
            if (!node || typeof IntersectionObserver === 'undefined') return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setInView(true);
                        if (triggerOnce) {
                            observer.disconnect();
                            observerRef.current = null;
                        }
                    } else if (!triggerOnce) {
                        setInView(false);
                    }
                },
                {threshold, rootMargin}
            );

            observer.observe(node);
            observerRef.current = observer;
        },
        [triggerOnce, threshold, rootMargin]
    );

    return [setRef, inView];
}
